import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Paper,
  Collapse,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';

const IndividualsPage = () => {
  const navigate = useNavigate();
  const { selectedBuilding } = useContext(AuthContext);

  const [individuals, setIndividuals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLocations, setExpandedLocations] = useState({});
  const [filterLocation, setFilterLocation] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [individualsResponse, locationsResponse] = await Promise.all([
          fetchWithAuth('/api/individuals'),
          fetchWithAuth('/api/locations')
        ]);

        if (individualsResponse.ok && locationsResponse.ok) {
          const individualsData = await individualsResponse.json();
          const locationsData = await locationsResponse.json();

          setIndividuals(Array.isArray(individualsData) ? individualsData : []);
          setLocations(locationsData.filter(loc => loc.name !== 'All'));

          // Expand all locations by default
          const expanded = {};
          locationsData.forEach(loc => {
            if (loc.name !== 'All') {
              expanded[loc.id] = true;
            }
          });
          setExpandedLocations(expanded);
        } else {
          setError('Failed to load data');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load individuals');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and group individuals by location
  const groupedIndividuals = useMemo(() => {
    let filtered = individuals;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ind =>
        ind.name?.toLowerCase().includes(term) ||
        ind.first_name?.toLowerCase().includes(term) ||
        ind.last_name?.toLowerCase().includes(term) ||
        ind.case_manager?.toLowerCase().includes(term)
      );
    }

    // Filter by selected location
    if (filterLocation !== 'all') {
      filtered = filtered.filter(ind => ind.building_location_id === parseInt(filterLocation));
    }

    // Group by location
    const grouped = {};

    // Initialize all locations
    locations.forEach(loc => {
      grouped[loc.id] = {
        location: loc,
        individuals: []
      };
    });

    // Add "Unassigned" group
    grouped['unassigned'] = {
      location: { id: 'unassigned', name: 'Unassigned' },
      individuals: []
    };

    // Group individuals
    filtered.forEach(ind => {
      const locId = ind.building_location_id || 'unassigned';
      if (grouped[locId]) {
        grouped[locId].individuals.push(ind);
      } else {
        grouped['unassigned'].individuals.push(ind);
      }
    });

    // Sort individuals within each group
    Object.values(grouped).forEach(group => {
      group.individuals.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    // Convert to array and filter out empty groups
    return Object.values(grouped).filter(group => group.individuals.length > 0);
  }, [individuals, locations, searchTerm, filterLocation]);

  const toggleLocation = (locationId) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locationId]: !prev[locationId]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    groupedIndividuals.forEach(group => {
      expanded[group.location.id] = true;
    });
    setExpandedLocations(expanded);
  };

  const collapseAll = () => {
    setExpandedLocations({});
  };

  const totalCount = useMemo(() => {
    return groupedIndividuals.reduce((sum, group) => sum + group.individuals.length, 0);
  }, [groupedIndividuals]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={600}>
            Individuals
          </Typography>
          <Chip
            label={`${totalCount} total`}
            color="primary"
            size="small"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/add-user')}
        >
          Add Individual
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search individuals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Location</InputLabel>
              <Select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                label="Filter by Location"
              >
                <MenuItem value="all">All Locations</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button size="small" onClick={expandAll}>
                Expand All
              </Button>
              <Button size="small" onClick={collapseAll}>
                Collapse All
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Grouped Individuals */}
      {groupedIndividuals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchTerm ? 'No individuals found matching your search.' : 'No individuals found.'}
          </Typography>
        </Paper>
      ) : (
        groupedIndividuals.map((group) => (
          <Card key={group.location.id} sx={{ mb: 2 }}>
            {/* Location Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
              onClick={() => toggleLocation(group.location.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon />
                <Typography variant="h6" fontWeight={600}>
                  {group.location.name}
                </Typography>
                <Chip
                  label={`${group.individuals.length} ${group.individuals.length === 1 ? 'individual' : 'individuals'}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
              <IconButton size="small" sx={{ color: 'white' }}>
                {expandedLocations[group.location.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Individuals List */}
            <Collapse in={expandedLocations[group.location.id]}>
              <CardContent sx={{ p: 0 }}>
                <Grid container>
                  {group.individuals.map((individual, index) => (
                    <Grid item xs={12} sm={6} md={4} key={individual.id}>
                      <Box
                        sx={{
                          p: 2,
                          borderBottom: 1,
                          borderRight: { sm: 1 },
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          transition: 'background-color 0.2s',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {individual.name?.charAt(0) || individual.first_name?.charAt(0) || '?'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={500}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {individual.name || `${individual.first_name} ${individual.last_name}`}
                          </Typography>
                          {individual.case_manager && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              CM: {individual.case_manager}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="View Profile">
                          <IconButton
                            size="small"
                            color="primary"
                            component={Link}
                            to={`/people/${individual.id}`}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        ))
      )}
    </Box>
  );
};

export default IndividualsPage;
